import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  CardTitle,
  CardText,
  FormGroup,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import * as dealersActions from '../../store/dealers/actions';
import { useDispatch, useSelector } from 'react-redux';
import useClipboard from 'react-hook-clipboard';
import { useTranslation } from 'react-i18next';
import toastr from 'toastr';

const GET_ITALIAN_COUNTRIES_URL =
  process.env.REACT_APP_WS_FUNCTION_URL + '/getItalianCountries';

const loadOptions = async (search, loadedOptions) => {
  if (search.length < 3) return [];

  //?search=&offset=0 || ?search=mu&offset=0
  const response = await fetch(
    `${GET_ITALIAN_COUNTRIES_URL}/?search=${encodeURIComponent(
      search.toLowerCase(),
    )}&offset=${loadedOptions.length}`,
  );
  const data = await response.json();

  return data.map(({ label, zipCode }) => ({
    label,
    value: zipCode,
  }));
};

const DealerFinder = ({ lead }) => {
  const [zipCode, setZipCode] = useState(lead?.zipCode);
  const [search, setSearch] = useState(null);

  const loading = useSelector((state) => state.Dealer.loading);
  const dealers = useSelector((state) => state.Dealer.dealers);

  const filteredDealers = useCallback(
    (search) => {
      if (!search) return dealers;
      return dealers.filter(({ company, address, place, city }) =>
        `${company} ${address} ${place} ${city}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
    },
    [dealers],
  );

  const [clipboard, copyToClipboard] = useClipboard();

  const dispatch = useDispatch();

  const findDealers = useCallback(() => {
    lead?.carBrand &&
      dispatch(
        dealersActions.syncDealers({
          carBrand: lead.carBrand,
          zipCode: zipCode,
        }),
      );
  }, [lead?.carBrand, zipCode]);

  useEffect(() => {
    findDealers();
  }, [findDealers]);

  const handleCopyDealerId = useCallback((dealerName, dealerId) => {
    copyToClipboard(dealerId);
    toastr.success(`Dealer ID copied to clipboard: ${dealerName}`);
  }, []);

  const onChangePlace = ({ value }) => {
    setZipCode(value);
    findDealers();
  };

  const { t } = useTranslation();

  return (
    <Card>
      <CardBody>
        {/* Sezione ricerca CAP */}
        <div className="mb-4">
          <div className="d-flex align-items-center">
            <div className="avatar-sm float-sm-left mr-3">
              <span className="avatar-title rounded-circle bg-info font-size-24">
                <i className="bx bx-car text-light"></i>
              </span>
            </div>
            <CardTitle className="mt-2 mr-3 font-size-18">
              {t('Find for place')}:
            </CardTitle>
            <div className="flex-grow-1">
              <FormGroup className="mb-0">
                <AsyncSelect
                  isMulti={false}
                  isSearchable={true}
                  loadOptions={loadOptions}
                  debounceTimeout={500}
                  loadOptionsOnMenuOpen={false}
                  onChange={onChangePlace}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Sezione filtro e risultati */}
        <div className="mb-2">
          <CardTitle className="font-size-16 mb-2">
            {t('Results for zip code')}:{' '}
            <span className="font-weight-bold">{zipCode || ''}</span>
          </CardTitle>
          <div className="d-flex align-items-center mb-3">
            <CardTitle className="mt-2 mr-3 font-size-16">
              {t('Search')}:
            </CardTitle>
            <div className="flex-grow-1">
              <FormGroup className="mb-0">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="form-control"
                  placeholder={t('Type to filter...')}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Lista risultati */}
        <Row>
          <Col
            md="12"
            className="overflow-auto"
            style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {!zipCode && (
              <div className="text-center">
                <span className="badge badge-warning text-dark font-size-13 my-3">
                  {t('Please select a place before searching for dealers')}
                </span>
              </div>
            )}
            {filteredDealers(search).length === 0 ? (
              <div className="text-center text-muted py-4">
                {t('No dealers found')}
              </div>
            ) : loading ? (
              <div className="text-center text-muted py-4">
                {t('Loading...')}
              </div>
            ) : (
              filteredDealers(search).map(
                ({ id, company, place, address, zipCode, city, dealerId }) => (
                  <Card key={id}>
                    <h5 className="card-header border-bottom text-uppercase">
                      {company}
                    </h5>
                    <CardBody>
                      <CardTitle className="mt-0">{place}</CardTitle>
                      <CardText>
                        {address}
                        <br />
                        {zipCode} - {city}
                      </CardText>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary waves-effect waves-light float-right"
                        onClick={() => handleCopyDealerId(company, dealerId)}>
                        {t('Copy Dealer ID')}
                      </button>
                    </CardBody>
                  </Card>
                ),
              )
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default DealerFinder;
